import { useEffect, useState } from 'react';
import axiosInstance from '../services/api/axiosConfig';

interface JobImage {
  url: string;
}

// 同一 job_id の重複フェッチを抑えるためのプロセス内キャッシュ。
const imageCache = new Map<string, JobImage[]>();

/**
 * 保存済み presigned URL は失効している可能性があるため、job_id を持つカードは
 * 表示時に GET /api/images/jobs/{job_id} で新しい url を取得し直す。
 *
 * @param jobId       生成ジョブID（無ければフェッチしない）
 * @param enabled     フェッチを行うか（completed かつ job_id ありのときだけ true）
 * @param fallback    フェッチ完了までに表示する（ストリーミングで受け取った）画像
 */
export function useImageJobImages(
  jobId: string | null | undefined,
  enabled: boolean,
  fallback: JobImage[] = []
): { images: JobImage[]; loading: boolean; error: boolean } {
  const cached = jobId ? imageCache.get(jobId) : undefined;
  const [images, setImages] = useState<JobImage[]>(cached ?? fallback);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    if (!enabled || !jobId) {
      return;
    }
    if (imageCache.has(jobId)) {
      setImages(imageCache.get(jobId) as JobImage[]);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(false);

    axiosInstance
      .get(`/images/jobs/${jobId}`)
      .then((response) => {
        if (cancelled) return;
        const fetched: JobImage[] = (response.data?.images ?? [])
          .filter((img: { url?: string }) => Boolean(img?.url))
          .map((img: { url: string }) => ({ url: img.url }));
        imageCache.set(jobId, fetched);
        setImages(fetched);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [jobId, enabled]);

  return { images, loading, error };
}

// テスト用: キャッシュをクリアする。
export function __clearImageJobCache(): void {
  imageCache.clear();
}
